/**
 * Centralized API Client with Automatic Token Refresh
 *
 * This module provides a fetch wrapper that automatically:
 * - Adds authentication headers to requests
 * - Detects expired tokens (401 responses)
 * - Refreshes tokens using the refresh token
 * - Retries the original request with the new token
 * - Handles event-level isolation
 */

import { clearLastRole, getPostLogoutPath } from 'utils/post-logout';
import * as sessionStore from 'utils/sessionStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface FetchWithAuthOptions extends RequestInit {
  skipAuth?: boolean;
  skipRetry?: boolean;
}

// ── API error contract ─────────────────────────────────────────────────
// Backend error responses have shape:
//   { code: number|string, error: string, params?: object, details?: FieldError[] }
// Use ApiError instead of `throw new Error(...)` so call sites can localize
// known codes via translateApiMessage and fall back to `fallbackMessage` for
// unmapped ones.

export interface ApiFieldError {
  field: string;
  code: number | string;
  params?: Record<string, unknown>;
}

export interface ApiErrorBody {
  code?: number | string;
  error?: string;
  message?: string;
  params?: Record<string, unknown>;
  details?: ApiFieldError[];
}

export class ApiError extends Error {
  /** Numeric (preferred) or string code identifying the message. */
  code: number | string;
  /** HTTP status of the response. */
  status: number;
  /** Optional interpolation values for the localized message. */
  params?: Record<string, unknown>;
  /** Optional field-level errors for forms. */
  details?: ApiFieldError[];
  /** English fallback the server sent — for unmapped codes / logging. */
  fallbackMessage: string;

  constructor(
    status: number,
    code: number | string,
    fallbackMessage: string,
    params?: Record<string, unknown>,
    details?: ApiFieldError[],
  ) {
    super(fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fallbackMessage = fallbackMessage;
    this.params = params;
    this.details = details;
  }
}

/**
 * Parse an error response body into an ApiError. Falls back gracefully when
 * the response isn't JSON or doesn't follow the contract.
 */
export async function parseApiError(response: Response, fallback?: string): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = await response.clone().json();
  } catch {
    // not JSON — leave body empty
  }
  const message = body.error || body.message || fallback || `Request failed (${response.status})`;
  const code = body.code ?? response.status;
  return new ApiError(response.status, code, message, body.params, body.details);
}

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let isLoggedOut = false;

/**
 * Get the current access token (sandbox-aware — the demo token on a demo tab,
 * the real localStorage token otherwise). See utils/sessionStore.
 */
function getAccessToken(): string | null {
  return sessionStore.getAccessToken();
}

/**
 * Get the current refresh token (sandbox-aware).
 */
function getRefreshToken(): string | null {
  return sessionStore.getRefreshToken();
}

/**
 * Store new tokens in the active scope (sandbox tab → sandbox keys; otherwise
 * the real localStorage keys). A refresh during a demo rewrites demo tokens
 * only — the real session is never touched.
 */
function setTokens(accessToken: string, refreshToken?: string): void {
  sessionStore.setTokens(accessToken, refreshToken);
}

/**
 * Clear tokens for the ACTIVE scope and signal logout (prevents an in-flight
 * refresh from re-writing tokens afterwards). Crucially scope-aware: inside a
 * demo this ends the sandbox scope ONLY and never touches the real localStorage
 * session — so a demo refresh-rejection can't log a paying couple out.
 */
export function clearTokens(): void {
  isLoggedOut = true;
  if (sessionStore.isSandboxActive()) {
    sessionStore.exitSandboxStorage();
  } else {
    sessionStore.clearRealSession();
    clearLastRole();
  }
}

/**
 * Reset the logged-out flag (call when a new session starts).
 */
export function resetLoggedOutFlag(): void {
  isLoggedOut = false;
}

/**
 * Refresh the access token using the refresh token
 * Returns the new access token
 */
export async function refreshAccessToken(): Promise<string> {
  if (isLoggedOut) {
    throw new Error('User has logged out');
  }

  // If already refreshing, wait for that promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      let response: Response;
      try {
        response = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch (networkError) {
        // Network error (offline, DNS failure, etc.) — don't clear tokens,
        // the user may come back online and retry successfully
        console.warn(
          '[API Client] Token refresh network error (not clearing session):',
          networkError,
        );
        throw networkError;
      }

      if (!response.ok) {
        // Server explicitly rejected the refresh token — session is truly invalid.
        // Read the role-aware destination BEFORE clearTokens() wipes the hint.
        console.error('[API Client] Token refresh rejected by server:', response.status);
        const target = getPostLogoutPath();
        clearTokens();
        window.location.href = target;
        throw new Error('Token refresh failed');
      }

      const data = await response.json();

      // If the user logged out while the refresh was in-flight, discard the new tokens
      if (isLoggedOut) {
        throw new Error('User logged out during token refresh');
      }

      setTokens(data.token, data.refreshToken);

      console.log('[API Client] Token refreshed successfully');
      return data.token;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Fetch wrapper with automatic token refresh
 *
 * @param url - The URL to fetch (can be relative or absolute)
 * @param options - Fetch options with additional skipAuth and skipRetry flags
 * @returns Promise<Response>
 */
export async function fetchWithAuth(
  url: string,
  options: FetchWithAuthOptions = {},
): Promise<Response> {
  const { skipAuth = false, skipRetry = false, ...fetchOptions } = options;

  // Ensure URL is absolute
  const absoluteUrl = url.startsWith('http') ? url : `${API_URL}${url}`;

  // Add auth header if not skipped
  if (!skipAuth) {
    let token = getAccessToken();

    // If no access token but refresh token exists, proactively refresh before making the request
    if (!token && !skipRetry && getRefreshToken()) {
      console.log(
        '[API Client] No access token but refresh token exists, refreshing proactively...',
      );
      try {
        token = await refreshAccessToken();
      } catch (error) {
        console.error('[API Client] Proactive token refresh failed:', error);
        // Will proceed without token — server will return 401
      }
    }

    if (token) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Authorization: `Bearer ${token}`,
      };
    }
  }

  // Make the request
  let response = await fetch(absoluteUrl, fetchOptions);

  // If 429 Too Many Requests, retry with exponential backoff (abort-signal aware)
  if (response.status === 429 && !skipRetry) {
    let retries = 0;
    const maxRetries = 10;

    while (response.status === 429 && retries < maxRetries) {
      // Respect abort signal
      if (fetchOptions.signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }

      retries++;
      const retryAfter = response.headers.get('Retry-After');
      let delay = 0;

      if (retryAfter) {
        // Retry-After can be seconds or a date string
        delay = isNaN(Number(retryAfter))
          ? Math.max(0, new Date(retryAfter).getTime() - Date.now())
          : Number(retryAfter) * 1000;
      } else {
        // Exponential backoff with jitter: 2^retries * 1000ms + random jitter
        delay = Math.pow(2, retries) * 1000 + Math.random() * 1000;
      }

      console.warn(
        `[API Client] Got 429. Retrying in ${delay}ms... (Attempt ${retries}/${maxRetries})`,
      );

      // Abort-aware delay
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, delay);
        fetchOptions.signal?.addEventListener(
          'abort',
          () => {
            clearTimeout(timer);
            reject(new DOMException('The operation was aborted.', 'AbortError'));
          },
          { once: true },
        );
      });

      response = await fetch(absoluteUrl, fetchOptions);
    }
  }

  // If 401 and we have a refresh token, try to refresh and retry
  if (response.status === 401 && !skipRetry && getRefreshToken()) {
    console.log('[API Client] Got 401, attempting token refresh...');

    try {
      const newToken = await refreshAccessToken();

      // Retry the original request with new token
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Authorization: `Bearer ${newToken}`,
      };

      response = await fetch(absoluteUrl, fetchOptions);
      console.log('[API Client] Retry after refresh:', response.status);
    } catch (error) {
      console.error('[API Client] Refresh and retry failed:', error);
      // Return the original 401 response
    }
  }

  return response;
}

/**
 * Helper to make authenticated GET requests
 */
export async function getWithAuth(url: string, options?: FetchWithAuthOptions): Promise<Response> {
  return fetchWithAuth(url, { ...options, method: 'GET' });
}

/**
 * Helper to make authenticated POST requests
 */
export async function postWithAuth(
  url: string,
  body?: any,
  options?: FetchWithAuthOptions,
): Promise<Response> {
  return fetchWithAuth(url, {
    ...options,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper to make authenticated PUT requests
 */
export async function putWithAuth(
  url: string,
  body?: any,
  options?: FetchWithAuthOptions,
): Promise<Response> {
  return fetchWithAuth(url, {
    ...options,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper to make authenticated PATCH requests
 */
export async function patchWithAuth(
  url: string,
  body?: any,
  options?: FetchWithAuthOptions,
): Promise<Response> {
  return fetchWithAuth(url, {
    ...options,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
}

/**
 * Helper to make authenticated DELETE requests
 */
export async function deleteWithAuth(
  url: string,
  options?: FetchWithAuthOptions,
): Promise<Response> {
  return fetchWithAuth(url, { ...options, method: 'DELETE' });
}
